import React, { useEffect, useRef, useState } from 'react';

// Dark NDVI palette — deep night greens so foreground text stays readable
const PALETTE = [
  '#050807', '#070C09', '#0A120C', '#0D1810',
  '#112015', '#16291A', '#1D3521', '#264428',
];

const PIN_LABELS = [
  'NDVI 0.84', 'NDVI 0.72', 'NDVI 0.91', 'NDVI 0.66',
  'NERO Chain', 'Effatha', 'Atvos', 'Morro Verde',
  'Agrominas', 'Mosaic', 'Syngenta',
  'Soja', 'Café', 'Cana', 'Milho', 'Algodao',
  'Cerrado', 'Pampa', 'Plot #014', 'Plot #221', 'Sensor 7',
];

const noise = (x, y, t) => {
  return (
    Math.sin(x * 0.42 + t) +
    Math.cos(y * 0.39 + t * 1.3) +
    Math.sin((x + y) * 0.21 + t * 0.7) +
    Math.cos(x * 1.6 + y * 0.7 + t * 1.4)
  ) * 0.25;
};

const SatelliteField = () => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const stateRef = useRef({ mouseX: -9999, mouseY: -9999, lastMove: 0, raf: 0 });
  const [pins, setPins] = useState([]);
  const [reticleVisible, setReticleVisible] = useState(false);
  const reticleRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const ctx = canvas.getContext('2d');
    let cellSize = 32;
    let cols = 0;
    let rows = 0;
    let raf = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = wrap.offsetWidth;
      const h = wrap.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cellSize = Math.max(22, Math.min(44, Math.round(w / 48)));
      cols = Math.ceil(w / cellSize) + 1;
      rows = Math.ceil(h / cellSize) + 1;
    };

    const onMouse = (e) => {
      const rect = wrap.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      stateRef.current.mouseX = mx;
      stateRef.current.mouseY = my;
      stateRef.current.lastMove = performance.now();
      const r = reticleRef.current;
      if (r) {
        r.style.transform = 'translate(' + mx + 'px,' + my + 'px) translate(-50%,-50%)';
      }
    };

    const onEnter = () => setReticleVisible(true);
    const onLeave = () => {
      setReticleVisible(false);
      stateRef.current.mouseX = -9999;
      stateRef.current.mouseY = -9999;
    };

    const draw = (now) => {
      const w = wrap.offsetWidth;
      const h = wrap.offsetHeight;
      const m = stateRef.current;
      const t = now * 0.00018;

      const idle = Math.min(1, (performance.now() - m.lastMove) / 2500);
      const heatRadius = 240 * (1 - idle * 0.35);

      ctx.fillStyle = '#050807';
      ctx.fillRect(0, 0, w, h);

      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const n = noise(x, y, t);
          let v = (n + 1) / 2;

          const dx = x * cellSize + cellSize / 2 - m.mouseX;
          const dy = y * cellSize + cellSize / 2 - m.mouseY;
          const d2 = dx * dx + dy * dy;
          if (d2 < heatRadius * heatRadius) {
            const d = Math.sqrt(d2);
            const heat = (1 - d / heatRadius);
            v = Math.min(1, v + heat * 0.35);
          }

          // Strong center vignette — center stays paper, edges show pattern
          const cx = w / 2;
          const cy = h / 2;
          const edx = (x * cellSize + cellSize / 2 - cx) / cx;
          const edy = (y * cellSize + cellSize / 2 - cy) / cy;
          const edge = Math.min(1, Math.sqrt(edx * edx + edy * edy));
          const ease = edge * edge * (3 - 2 * edge);
          v = v * (0.8 + ease * 1);

          const idx = Math.min(PALETTE.length - 1, Math.max(0, Math.floor(v * PALETTE.length)));
          ctx.fillStyle = PALETTE[idx];
          ctx.fillRect(x * cellSize, y * cellSize, cellSize - 1, cellSize - 1);
        }
      }

      raf = requestAnimationFrame(draw);
      stateRef.current.raf = raf;
    };

    resize();
    window.addEventListener('resize', resize);
    wrap.addEventListener('mousemove', onMouse);
    wrap.addEventListener('mouseenter', onEnter);
    wrap.addEventListener('mouseleave', onLeave);
    raf = requestAnimationFrame(draw);

    let pinTimer;
    const schedule = () => {
      const delay = 1400 + Math.random() * 1800;
      pinTimer = setTimeout(() => {
        const id = Math.random().toString(36).slice(2, 9);
        // pins spawn nearer edges (where the canvas is more visible) — avoid the calm center
        const side = Math.random();
        const x = side < 0.5 ? 0.05 + Math.random() * 0.22 : 0.73 + Math.random() * 0.22;
        const y = 0.18 + Math.random() * 0.6;
        const label = PIN_LABELS[Math.floor(Math.random() * PIN_LABELS.length)];
        setPins((prev) => [...prev.slice(-5), { id, x, y, label }]);
        setTimeout(() => {
          setPins((prev) => prev.filter((p) => p.id !== id));
        }, 3800);
        schedule();
      }, delay);
    };
    schedule();

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(pinTimer);
      window.removeEventListener('resize', resize);
      wrap.removeEventListener('mousemove', onMouse);
      wrap.removeEventListener('mouseenter', onEnter);
      wrap.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <div ref={wrapRef} className="absolute inset-0 overflow-hidden">
      <canvas ref={canvasRef} className="block w-full h-full" aria-hidden="true" />

      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,8,7,0.9)_0%,rgba(5,8,7,0.5)_35%,rgba(5,8,7,0.05)_70%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-night-950 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-night-950 to-transparent" />

      {pins.map((p) => (
        <div
          key={p.id}
          className="ati-pin"
          style={{ left: (p.x * 100) + '%', top: (p.y * 100) + '%' }}
        >
          <span className="ati-pin-line" />
          <span className="ati-pin-label">
            <span className="ati-pin-dot" />
            {p.label}
          </span>
        </div>
      ))}

      <div
        ref={reticleRef}
        className={'ati-reticle ' + (reticleVisible ? 'is-visible' : '')}
        aria-hidden="true"
      />
    </div>
  );
};

export default SatelliteField;
